import { useState, useEffect, useRef } from 'react';

export default function AIRoadmap() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-32 px-6 relative overflow-hidden bg-slate-50" id="ai-roadmap">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#06b6d4]/5 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#2a4b7c]/5 rounded-full blur-[120px]"></div>
      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`text-center mb-24 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-[#2a4b7c]">
            AI-Ready Skill Intelligence (Roadmap)
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Tương lai của quản trị năng lực với công nghệ AI
          </p>
        </div>
        <div className="relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#2a4b7c] via-[#06b6d4] to-[#2ecc71] hidden md:block"></div>
          <div className="space-y-24">
            {/* Item 1 - Auto Skill Inference */}
            <div className={`relative flex flex-col md:flex-row items-center justify-center gap-12 group transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <div className="w-full md:w-5/12 text-center md:text-right order-2 md:order-1">
                <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 border-l-4 border-l-[#2a4b7c] md:border-l-0 md:border-r-4 md:border-r-[#2a4b7c] hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-[#2a4b7c] mb-4">Auto Skill Inference</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Tự động suy luận kỹ năng từ CV, dự án và hoạt động làm việc
                  </p>
                </div>
              </div>
              <div className="relative z-20 order-1 md:order-2">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-xl border-2 border-[#2a4b7c]/30 relative group hover:scale-110 transition-transform duration-300">
                  <div className="absolute inset-0 rounded-full bg-[#2a4b7c]/10 animate-pulse"></div>
                  <i className="ri-brain-line text-[#2a4b7c] text-5xl relative z-10 group-hover:scale-110 transition-transform duration-300"></i>
                </div>
              </div>
              <div className="w-full md:w-5/12 order-3 hidden md:block"></div>
            </div>

            {/* Item 2 - Future Skill Prediction */}
            <div className={`relative flex flex-col md:flex-row items-center justify-center gap-12 group transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <div className="w-full md:w-5/12 order-3 md:order-1 hidden md:block"></div>
              <div className="relative z-20 order-1">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-xl border-2 border-[#06b6d4]/30 relative group hover:scale-110 transition-transform duration-300">
                  <div className="absolute inset-0 rounded-full bg-[#06b6d4]/10 animate-pulse"></div>
                  <i className="ri-line-chart-line text-[#06b6d4] text-5xl relative z-10 group-hover:scale-110 transition-transform duration-300"></i>
                </div>
              </div>
              <div className="w-full md:w-5/12 text-center md:text-left order-2">
                <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 border-l-4 border-l-[#06b6d4] hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-[#2a4b7c] mb-4">Future Skill Prediction</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Dự báo nhu cầu kỹ năng tương lai dựa trên xu hướng ngành
                  </p>
                </div>
              </div>
            </div>

            {/* Item 3 - Personalized Learning */}
            <div className={`relative flex flex-col md:flex-row items-center justify-center gap-12 group transition-all duration-1000 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <div className="w-full md:w-5/12 text-center md:text-right order-2 md:order-1">
                <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 border-l-4 border-l-[#2ecc71] md:border-l-0 md:border-r-4 md:border-r-[#2ecc71] hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-[#2a4b7c] mb-4">Personalized Learning</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Gợi ý lộ trình phát triển cá nhân hóa cho từng nhân viên
                  </p>
                </div>
              </div>
              <div className="relative z-20 order-1 md:order-2">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-xl border-2 border-[#2ecc71]/30 relative group hover:scale-110 transition-transform duration-300">
                  <div className="absolute inset-0 rounded-full bg-[#2ecc71]/10 animate-pulse"></div>
                  <i className="ri-user-heart-line text-[#2ecc71] text-5xl relative z-10 group-hover:scale-110 transition-transform duration-300"></i>
                </div>
              </div>
              <div className="w-full md:w-5/12 order-3 hidden md:block"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
